import React from "react";
import { WifiOff, Wifi, RotateCw, ServerCrash } from "lucide-react";

/**
 * Persistent, sticky offline/reconnected banner.
 * Renders at the very top of the viewport (z-[9999]) so it sits above everything.
 * Shows retry queue status when requests are queued/flushing.
 *
 * Props:
 *   isOnline      , true when connected
 *   wasOffline    , true for ~4 s after reconnecting
 *   retryQueue    , { pending, flushing } from useRetryQueue
 *   serverDown    , true when the backend is briefly unreachable (browser still online)
 *   onRetryServer , callback to immediately re-check the backend
 */
export default function OfflineBanner({ isOnline, wasOffline, retryQueue = {}, serverDown = false, onRetryServer }) {
  const { pending = 0, flushing = false } = retryQueue;

  // Server-unreachable: a GENTLE, non-blocking pill in the bottom corner (not a
  // big alarming top bar). It never blocks the page, and by the time it shows,
  // App.js has already confirmed the backend truly failed two health-pings.
  if (isOnline && serverDown) {
    return (
      <div data-testid="server-reconnect-banner" role="status" aria-live="polite"
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none animate-[fadeInUp_.3s_ease]">
        <style>{`@keyframes fadeInUp{from{opacity:0;transform:translate(-50%,12px)}to{opacity:1;transform:translate(-50%,0)}}`}</style>
        <div className="pointer-events-auto flex items-center gap-2.5 pl-3.5 pr-2 py-2 rounded-full text-xs font-semibold"
          style={{
            background: "rgba(30,41,59,0.96)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
            backdropFilter: "blur(8px)",
          }}>
          <RotateCw size={13} className="text-amber-300/90 animate-spin" />
          <span className="text-white/85">Reconnecting…</span>
          <button onClick={onRetryServer} data-testid="server-reconnect-retry"
            className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 active:scale-95 text-white/90 text-[11px] font-bold rounded-full px-2.5 py-1 transition-all">
            <ServerCrash size={12} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // Nothing to show when user has been online the whole time and no queue activity
  if (isOnline && !wasOffline && pending === 0 && !flushing) return null;

  const offline = !isOnline;

  return (
    <div
      data-testid="offline-banner"
      role="alert"
      aria-live="assertive"
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-500 ${
        offline || wasOffline || flushing
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0"
      }`}
    >
      {offline ? (
        /* ── Offline state ── */
        <div
          className="flex items-center justify-center gap-3 px-4 py-3 text-sm font-semibold"
          style={{
            background:
              "linear-gradient(90deg, rgba(220,38,38,0.92) 0%, rgba(185,28,28,0.92) 50%, rgba(153,27,27,0.92) 100%)",
            
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          <WifiOff size={16} className="text-white/90 animate-pulse" />
          <span className="text-white tracking-wide">
            You're offline, reconnect to continue your adventure
          </span>
          {pending > 0 && (
            <span data-testid="offline-queue-count" className="hidden sm:inline text-white/70 text-xs ml-2 bg-white/10 px-2 py-0.5 rounded-full">
              {pending} {pending === 1 ? "request" : "requests"} saved
            </span>
          )}
          {pending === 0 && (
            <span className="hidden sm:inline text-white/50 text-xs ml-2">
              We'll pick up right where you left off
            </span>
          )}
        </div>
      ) : flushing ? (
        /* ── Retrying queued requests ── */
        <div
          data-testid="offline-retrying"
          className="flex items-center justify-center gap-3 px-4 py-3 text-sm font-semibold"
          style={{
            background:
              "linear-gradient(90deg, rgba(59,130,246,0.92) 0%, rgba(37,99,235,0.92) 50%, rgba(29,78,216,0.92) 100%)",
            
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          <RotateCw size={16} className="text-white/90 animate-spin" />
          <span className="text-white tracking-wide">
            Retrying {pending} saved {pending === 1 ? "request" : "requests"}...
          </span>
        </div>
      ) : (
        /* ── Reconnected state (shows briefly) ── */
        <div
          className="flex items-center justify-center gap-3 px-4 py-3 text-sm font-semibold"
          style={{
            background:
              "linear-gradient(90deg, rgba(22,163,74,0.92) 0%, rgba(21,128,61,0.92) 50%, rgba(20,83,45,0.92) 100%)",
            
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.4)",
          }}
        >
          <Wifi size={16} className="text-white/90" />
          <span className="text-white tracking-wide">
            Back online, welcome back, explorer!
          </span>
        </div>
      )}
    </div>
  );
}
